import { useEffect, useState } from 'react'

import styles from './EventEditor.module.scss'
import sharedStyles from '@/pages/app/AnalyticsPortal/styles.module.scss'

import type { AnalyticsPlatform } from '@/api/generated/api'
import Button from '@/components/Button'
import Checkbox, { CheckboxGroup } from '@/components/Checkbox'
import DialogWrapper from '@/components/Dialog'
import Input from '@/components/Input'
import { NEW_EVENT_ID_PREFIX, PLATFORMS } from '@/pages/app/AnalyticsPortal/constants'
import type { AnalyticsEvent } from '@/pages/app/AnalyticsPortal/types'

interface EventEditorProps {
  event: AnalyticsEvent
  existingNames: string[]
  onSave: (event: AnalyticsEvent) => void
  onCancel: () => void
}

const MAX_EVENT_NAME_LENGTH = 255

const PROPERTY_TYPES = [
  { type: 'string', description: 'Text values' },
  { type: 'number', description: 'Numeric values (integers or decimals)' },
  { type: 'boolean', description: 'True/false values' },
  { type: 'array', description: 'List of values' },
  { type: 'object', description: 'Nested object' },
] as const

const nameToEventKey = (name: string): string => {
  return name
    .slice(0, MAX_EVENT_NAME_LENGTH)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^([0-9])/, '_$1')
}

const isEmptySchema = (schema: unknown): boolean =>
  typeof schema === 'object' && schema !== null && Object.keys(schema).length === 0

const formatSchemaText = (schema: unknown): string =>
  isEmptySchema(schema) ? '' : JSON.stringify(schema, null, 2)

const EventEditor = ({ event, existingNames, onSave, onCancel }: EventEditorProps) => {
  const [formData, setFormData] = useState<AnalyticsEvent>(event)
  const [jsonText, setJsonText] = useState(formatSchemaText(event.properties_schema))
  const [jsonError, setJsonError] = useState<string | null>(null)
  const [nameError, setNameError] = useState<string | null>(null)

  const isNew = event.id.startsWith(NEW_EVENT_ID_PREFIX)

  useEffect(() => {
    setFormData(event)
    setJsonText(formatSchemaText(event.properties_schema))
  }, [event])

  // to ensure naming conventions are followed --> TODO(advaith): to be updated with taxonomy rules later
  const getNameError = (value: string): string | null => {
    if (!value.trim()) return 'Name is required'
    if (value.length > MAX_EVENT_NAME_LENGTH) return `Name must be ${MAX_EVENT_NAME_LENGTH} characters or less`
    if (existingNames.includes(value) && (isNew || value !== event.name))
      return 'An event with this name already exists'
    if (isNew) {
      const generatedKey = nameToEventKey(value)
      if (!generatedKey) return 'Name must contain at least one letter or number'
    }
    return null
  }

  const handleNameChange = (value: string) => {
    setFormData((prev) => ({ ...prev, name: value }))
    setNameError(getNameError(value))
  }

  const handleJsonChange = (value: string) => {
    setJsonText(value)
    if (!value.trim()) {
      setFormData((prev) => ({ ...prev, properties_schema: {} }))
      setJsonError(null)
      return
    }
    try {
      const parsed = JSON.parse(value)
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        setJsonError('Properties schema must be an object')
        return
      }
      setFormData((prev) => ({ ...prev, properties_schema: parsed }))
      setJsonError(null)
    } catch {
      setJsonError('Invalid JSON')
    }
  }

  const handlePlatformToggle = (platform: AnalyticsPlatform) => {
    setFormData((prev) => {
      const currentPlatforms = prev.platforms ?? []
      return {
        ...prev,
        platforms: currentPlatforms.includes(platform)
          ? currentPlatforms.filter((p) => p !== platform)
          : [...currentPlatforms, platform],
      }
    })
  }

  const isFormValid = () => {
    if (nameError || jsonError) return false
    if (!formData.name.trim()) return false
    if (!formData.description?.trim()) return false
    if (!formData.trigger?.trim()) return false
    if ((formData.platforms ?? []).length === 0) return false
    if (isNew && !nameToEventKey(formData.name)) return false
    return true
  }

  const handleSubmit = () => {
    if (!isFormValid()) {
      return
    }
    const eventToSave = isNew ? { ...formData, event_key: nameToEventKey(formData.name) } : formData
    onSave(eventToSave)
  }

  return (
    <DialogWrapper isOpen={true} title={isNew ? 'Add Event' : 'Edit Event'} close={onCancel} minWidth={560}>
      <div className={sharedStyles.dialogBody}>
        <div className={sharedStyles.formGroup}>
          <label className={sharedStyles.label}>Name *</label>
          <Input type="text" value={formData.name} onInput={handleNameChange} placeholder="e.g., User Signed Up" />
          {nameError && <span className={sharedStyles.errorText}>{nameError}</span>}
        </div>

        <div className={sharedStyles.formGroup}>
          <label className={sharedStyles.label}>Description *</label>
          <textarea
            className={sharedStyles.textarea}
            value={formData.description || ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value || null }))}
            placeholder="Describe what this event tracks..."
          />
        </div>

        <div className={sharedStyles.formGroup}>
          <label className={sharedStyles.label}>Trigger *</label>
          <span className={sharedStyles.helperText}>When does this event fire?</span>
          <textarea
            className={sharedStyles.textarea}
            value={formData.trigger || ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, trigger: e.target.value || null }))}
            placeholder="e.g., When user clicks the signup button..."
          />
        </div>

        <div className={sharedStyles.formGroup}>
          <label className={sharedStyles.label}>Properties Schema (JSON)</label>
          <span className={sharedStyles.helperText}>Define the event properties as a JSON object</span>
          <textarea
            className={`${styles.jsonEditor} ${jsonError ? styles.jsonError : ''}`}
            value={jsonText}
            onChange={(e) => handleJsonChange(e.target.value)}
            placeholder='e.g., {"pageId": "string", "count": "number"}'
          />
          {jsonError && <span className={sharedStyles.errorText}>{jsonError}</span>}
          <div className={styles.propertyTypes}>
            <span className={sharedStyles.helperText}>Available types:</span>
            {PROPERTY_TYPES.map(({ type, description }) => (
              <span key={type} className={`${sharedStyles.lifecycleBadge} ${sharedStyles.draft}`} title={description}>
                {type}
              </span>
            ))}
          </div>
        </div>

        <div className={sharedStyles.formGroup}>
          <label className={sharedStyles.label}>Platforms *</label>
          <CheckboxGroup>
            {PLATFORMS.map((platform) => (
              <Checkbox
                key={platform}
                checked={(formData.platforms ?? []).includes(platform)}
                onChange={() => handlePlatformToggle(platform)}
                label={platform}
              />
            ))}
          </CheckboxGroup>
        </div>

        <div className={sharedStyles.dialogFooter}>
          <Button title="Cancel" onClick={onCancel} type="light" />
          <Button title={isNew ? 'Add' : 'Save'} onClick={handleSubmit} type="dark" isDisabled={!isFormValid()} />
        </div>
      </div>
    </DialogWrapper>
  )
}

export default EventEditor
